import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CanAccess,
  useDataProvider,
  useTranslate,
  type Identifier,
} from "ra-core";
import { TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

import { StatusPill } from "../design/StatusPill";
import type { CrmDataProvider } from "../providers/types";
import type { QuoterPlanApprover, Sale } from "../types";
import { QUOTER_QUERY_KEY } from "./quoterQueryKey";

const FETCH_ALL = { page: 1, perPage: 1000 };
const SELECT_CLASS =
  "h-11 w-full rounded-md border bg-background px-3 text-sm sm:h-9";

const fullName = (sale: Sale | undefined) =>
  sale ? `${sale.first_name} ${sale.last_name}`.trim() : "-";

/**
 * Almikia batch EQ3 (TASK-115): the approver of custom plans, direccion only. Lists the approvers,
 * adds one and (de)activates one through `quoter_set_plan_approver` (audited in SQL; a row is never
 * deleted), and shows the "Sin aprobador activo" warning while no approver is active (custom plans
 * then stay pending). Any other role renders nothing.
 */
export const QuoterApproversAdmin = () => (
  <CanAccess resource="quoter_plan_approvers" action="list">
    <ApproversContent />
  </CanAccess>
);

const ApproversContent = () => {
  const translate = useTranslate();
  const t = (key: string) => translate(`crm.quoter.approval.${key}`);
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [picked, setPicked] = useState("");
  const [error, setError] = useState<string | null>(null);

  const approvers = useQuery({
    queryKey: [QUOTER_QUERY_KEY, "plan-approvers"],
    queryFn: async () => {
      const [rows, sales] = await Promise.all([
        dataProvider.getList<QuoterPlanApprover>("quoter_plan_approvers", {
          pagination: FETCH_ALL,
          sort: { field: "id", order: "ASC" },
          filter: {},
        }),
        dataProvider.getList<Sale>("sales", {
          pagination: FETCH_ALL,
          sort: { field: "first_name", order: "ASC" },
          filter: {},
        }),
      ]);
      return { rows: rows.data, sales: sales.data };
    },
  });
  const hasActive = useQuery({
    queryKey: [QUOTER_QUERY_KEY, "has-active-approver"],
    queryFn: () => dataProvider.quoterHasActivePlanApprover(),
  });

  const change = async (salesId: Identifier, active: boolean) => {
    setError(null);
    try {
      await dataProvider.quoterSetPlanApprover(salesId, active);
      setPicked("");
      await queryClient.invalidateQueries({ queryKey: [QUOTER_QUERY_KEY] });
    } catch (e) {
      setError(e instanceof Error ? e.message : t("action_error"));
    }
  };

  const rows = approvers.data?.rows ?? [];
  const sales = approvers.data?.sales ?? [];
  const candidates = sales.filter(
    (sale) => !sale.disabled && !rows.some((row) => row.sales_id === sale.id),
  );

  return (
    <section className="space-y-3" aria-label={t("approvers_title")}>
      {hasActive.data === false ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
        >
          <TriangleAlert
            className="mt-0.5 size-4 shrink-0"
            aria-hidden="true"
          />
          <div>
            <p className="font-medium">{t("no_approver_title")}</p>
            <p>{t("no_approver_warning")}</p>
          </div>
        </div>
      ) : null}
      <h2 className="text-lg font-semibold">{t("approvers_title")}</h2>
      <p className="text-sm text-muted-foreground">{t("approvers_help")}</p>
      <Card>
        <CardContent className="space-y-3 p-4">
          {rows.length === 0 && !approvers.isPending ? (
            <p className="text-sm text-muted-foreground">
              {t("approver_none")}
            </p>
          ) : (
            <ul className="space-y-2" data-testid="plan-approvers">
              {rows.map((row) => (
                <li
                  key={row.id}
                  className="flex flex-wrap items-center justify-between gap-2"
                >
                  <span className="font-medium">
                    {fullName(sales.find((sale) => sale.id === row.sales_id))}
                  </span>
                  <span className="flex flex-wrap items-center gap-2">
                    <StatusPill
                      tone={row.active ? "ok" : "not-ok"}
                      text={t(
                        row.active ? "approver_active" : "approver_inactive",
                      )}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 sm:h-9"
                      onClick={() => change(row.sales_id, !row.active)}
                    >
                      {t(
                        row.active
                          ? "approver_deactivate"
                          : "approver_activate",
                      )}
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="space-y-2">
            <Label htmlFor="plan-approver-pick">{t("approver_pick")}</Label>
            <select
              id="plan-approver-pick"
              className={SELECT_CLASS}
              value={picked}
              onChange={(e) => setPicked(e.target.value)}
            >
              <option value="">{t("approver_pick_placeholder")}</option>
              {candidates.map((sale) => (
                <option key={sale.id} value={String(sale.id)}>
                  {fullName(sale)}
                </option>
              ))}
            </select>
            <Button
              type="button"
              className="h-11 sm:h-9"
              disabled={picked === ""}
              onClick={() =>
                change(
                  candidates.find((sale) => String(sale.id) === picked)!.id,
                  true,
                )
              }
            >
              {t("approver_add")}
            </Button>
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </CardContent>
      </Card>
    </section>
  );
};
